/**
 * AppICEMAP — DetailSheet Component
 * Full-screen report detail overlay with photo gallery, confirmation bar,
 * comments, and sharing.
 *
 * Tag: <detail-sheet>
 *
 * Properties:
 *   .report   — report object (null to hide)
 *   .open     — boolean
 *
 * Dispatches:
 *   detail-sheet:close         → { }
 *   detail-sheet:vote          → { reportId, type }
 *   detail-sheet:comment       → { reportId, body, parentId }
 */

import { state } from "../../store/index.js";
import { getCategoryStyle } from "../../utils/categoryStyle.js";
import { fetchComments, createComment } from "../../api/comments.js";
import { fetchUserVote, createConfirmation } from "../../api/confirmations.js";
import { show as showToast } from "../shared/Toast.js";
import { fetchReportPhotos } from "../../api/reports.js";
import { COMMENTS_ENABLED } from "../../config/features.js";
import { getFileUrl } from "../../utils/image.js";
import {
	canDelete,
	confirmAndDeleteReport,
} from "../../services/reportActions.js";

export class DetailSheet extends HTMLElement {
	constructor() {
		super();
		this._report = null;
		this._open = false;
		this._comments = [];
		this._isLoadingComments = false;
		this._userVote = null;
		this._isVoting = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get report() {
		return this._report;
	}
	set report(val) {
		this._report = val;
		if (this._open && this._report) {
			this._loadReportData();
		}
	}

	get open() {
		return this._open;
	}
	set open(val) {
		this._open = !!val;
		if (this._open) {
			this._show();
			if (this._report) this._loadReportData();
		} else {
			this._hide();
		}
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="detail-sheet__backdrop"></div>
      <div class="detail-sheet" role="dialog" aria-modal="true" aria-label="Detalle del reporte">
        <div class="detail-sheet__header">
          <button class="btn--icon" aria-label="Cerrar" data-action="close">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
          <div class="detail-sheet__badges"></div>
        </div>
        <div class="detail-sheet__scroll">
          <div class="detail-sheet__hero"></div>
          <div class="detail-sheet__body"></div>
          <confirmation-bar></confirmation-bar>
          <div class="detail-sheet__section">
            <h4 class="detail-sheet__section-title">Comentarios</h4>
            ${
							COMMENTS_ENABLED
								? "<comment-thread></comment-thread>"
								: `<div class="coming-soon">
              <i class="ph ph-chats-circle" aria-hidden="true"></i>
              <p class="coming-soon__title">Los comentarios llegan pronto</p>
              <p class="coming-soon__text">
                Aquí podrás comentar el reporte y responder a otros vecinos.
                Mientras tanto, usa «Sigue activo» o «Ya no está» para avisar
                a la comunidad.
              </p>
            </div>`
						}
          </div>
          <div class="detail-sheet__section">
            <button class="detail-sheet__share" data-action="share">
              <i class="ph ph-share-network" aria-hidden="true"></i>
              Compartir reporte
            </button>
            <button class="delete-report-btn detail-sheet__delete" data-action="delete" hidden>
              <i class="ph ph-trash" aria-hidden="true"></i>
              Borrar reporte
            </button>
          </div>
        </div>
        ${COMMENTS_ENABLED ? "<comment-input></comment-input>" : ""}
      </div>
    `;

		this._backdrop = this.querySelector(".detail-sheet__backdrop");
		this._sheetEl = this.querySelector(".detail-sheet");
		this._headerBadges = this.querySelector(".detail-sheet__badges");
		this._heroEl = this.querySelector(".detail-sheet__hero");
		this._bodyEl = this.querySelector(".detail-sheet__body");
		this._confirmBar = this.querySelector("confirmation-bar");
		this._commentThread = this.querySelector("comment-thread");
		this._commentInput = this.querySelector("comment-input");
		this._shareBtn = this.querySelector('[data-action="share"]');
		this._deleteBtn = this.querySelector('[data-action="delete"]');
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		// Close button
		const closeBtn = this.querySelector('[data-action="close"]');
		if (closeBtn) {
			closeBtn.addEventListener("click", () => this._close());
		}

		// Backdrop click
		if (this._backdrop) {
			this._backdrop.addEventListener("click", () => this._close());
		}

		// Escape key
		this.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this._open) {
				this._close();
			}
		});

		// Confirmation votes
		if (this._confirmBar) {
			this._confirmBar.addEventListener("confirmation-bar:vote", (e) => {
				this._handleVote(e.detail.type);
			});
		}

		// Comments
		if (this._commentThread) {
			this._commentThread.addEventListener("comment-thread:reply", (e) => {
				if (this._commentInput) {
					this._commentInput.parentId = e.detail.commentId;
					this._commentInput.replyToAuthor = e.detail.authorName;
					this._commentInput.focus();
				}
			});
		}

		if (this._commentInput) {
			this._commentInput.addEventListener("comment-input:submit", (e) => {
				this._handleComment(e.detail.body, e.detail.parentId);
			});

			this._commentInput.addEventListener("comment-input:cancel-reply", () => {
				if (this._commentInput) {
					this._commentInput.clearReply();
				}
			});
		}

		// Share
		if (this._deleteBtn) {
			this._deleteBtn.addEventListener("click", async () => {
				const deleted = await confirmAndDeleteReport(this._report);
				// The report no longer exists: close the sheet behind the user
				if (deleted) this._close();
			});
		}

		if (this._shareBtn) {
			this._shareBtn.addEventListener("click", () => this._shareReport());
		}
	}

	/* ── Show / Hide ───────────────────────────────────────────────────────── */

	_show() {
		if (this._sheetEl) {
			this._sheetEl.classList.add("detail-sheet--open");
		}
		if (this._backdrop) {
			this._backdrop.classList.add("detail-sheet__backdrop--visible");
		}
		document.body.style.overflow = "hidden";
	}

	_hide() {
		if (this._sheetEl) {
			this._sheetEl.classList.remove("detail-sheet--open");
		}
		if (this._backdrop) {
			this._backdrop.classList.remove("detail-sheet__backdrop--visible");
		}
		document.body.style.overflow = "";
	}

	_close() {
		this.open = false;
		this.dispatchEvent(
			new CustomEvent("detail-sheet:close", { bubbles: true }),
		);
	}

	/* ── Load Data ─────────────────────────────────────────────────────────── */

	async _loadReportData() {
		if (!this._report) return;

		this._renderReport();
		this._loadPhotos();
		this._loadComments();
		this._loadUserVote();
	}

	/**
	 * Photos live in their own collection, so they need a second request. The
	 * sheet renders immediately without them and fills the gallery in when they
	 * arrive.
	 */
	async _loadPhotos() {
		const reportId = this._report?.id;

		// El reporte suele traer ya las fotos por el alias O2M; solo se consulta
		// aparte si viniera sin ellas (p. ej. desde una caché antigua).
		const inline = Array.isArray(this._report?.photos) ? this._report.photos : null;
		this._photos = [];

		const rows = inline?.length ? inline : await fetchReportPhotos(reportId);

		// The sheet may have moved on to another report meanwhile
		if (this._report?.id !== reportId) return;

		this._photos = rows
			.filter((row) => row.image)
			.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
			.map((row) => ({ id: row.id, image: { url: getFileUrl(row.image) } }));

		if (this._photos.length > 0) this._renderReport();
	}

	_renderReport() {
		const r = this._report;
		const cat = r.category || {};
		const { color } = getCategoryStyle(cat);

		// Delete action — admins only
		if (this._deleteBtn) this._deleteBtn.hidden = !canDelete(r);

		// Header badges
		if (this._headerBadges) {
			let badgesHtml = `<span class="badge badge--category" style="--badge-color:${color}">${escapeHtml(cat.label || "Reporte")}</span>`;

			if (r.status === "expiring") {
				badgesHtml += `<span class="badge badge--expiring">Expirado</span>`;
			} else if (r.status === "expired") {
				badgesHtml += `<span class="badge badge--expired">Expirado</span>`;
			} else if (r.status === "resolved") {
				badgesHtml += `<span class="badge badge--resolved">Resuelto</span>`;
			}

			this._headerBadges.innerHTML = badgesHtml;
		}

		// Hero: photos first, then a map of where the report is. With no photos
		// the map takes the whole frame instead of an empty image placeholder.
		if (this._heroEl) {
			const photos = this._photos || [];
			const coords = r.location?.coordinates;
			const location = Array.isArray(coords)
				? { lat: coords[1], lng: coords[0], category: cat }
				: null;

			if (photos.length > 0 || location) {
				this._heroEl.innerHTML = "";
				const gallery = document.createElement("photo-gallery");
				gallery.photos = photos;
				gallery.location = location;
				this._heroEl.appendChild(gallery);
			} else {
				this._heroEl.innerHTML = `
          <div class="detail-sheet__hero-placeholder">
            <i class="ph ph-image" aria-hidden="true"></i>
          </div>
        `;
			}
		}

		// Body
		if (this._bodyEl) {
			const user = r.user_created || {};
			const displayName = getDisplayName(r);
			const timeAgo = relativeTime(r.created_at);
			const isLowK = isLowKarma(r);
			const isAnon = r.display_name_mode === "anonymous";

			this._bodyEl.innerHTML = `
        <h3 class="detail-sheet__title">${escapeHtml(r.title || "Sin título")}</h3>
        ${r.description ? `<p class="detail-sheet__description">${escapeHtml(r.description)}</p>` : ""}
        <div class="detail-sheet__meta">
          <div class="detail-sheet__meta-row">
            <span class="detail-sheet__author">${escapeHtml(displayName)}</span>
            ${isAnon ? `<span class="badge badge--anonymous">Anónimo</span>` : ""}
            ${isLowK ? `<span class="badge badge--low-karma">⚠️ Baja confianza</span>` : ""}
            ${user.karma > 0 ? `<span class="badge badge--accent-100"><i class="ph ph-star"></i> ${user.karma} karma</span>` : ""}
          </div>
          <div class="detail-sheet__meta-row">
            <span>${timeAgo}</span>
            ${r.address ? `<span>· ${escapeHtml(r.address)}</span>` : ""}
          </div>
          ${r.location_precision === "approx" ? `<div class="detail-sheet__meta-row">🔒 Ubicación aproximada</div>` : ""}
        </div>
      `;
		}

		// Confirmation bar
		if (this._confirmBar) {
			this._confirmBar.report = r;
			this._confirmBar.userVote = this._userVote;

			// Check if own report
			const currentUser = state.user;
			const isOwn =
				currentUser && r.user_created && currentUser.id === r.user_created.id;
			this._confirmBar.isOwnReport = isOwn;
		}

		// Reset comment input
		if (this._commentInput) {
			this._commentInput.clearReply();
		}
	}

	async _loadComments() {
		if (!COMMENTS_ENABLED) return;
		if (!this._report) return;

		this._isLoadingComments = true;
		if (this._commentThread) {
			this._commentThread.loading = true;
		}

		try {
			this._comments = await fetchComments(this._report.id);
		} catch (err) {
			console.warn("[DetailSheet] Failed to load comments:", err);
			this._comments = [];
		}

		this._isLoadingComments = false;
		if (this._commentThread) {
			this._commentThread.loading = false;
			this._commentThread.comments = this._comments;
		}
	}

	async _loadUserVote() {
		if (!this._report) return;

		try {
			const vote = await fetchUserVote(this._report.id);
			this._userVote = vote ? vote.type : null;
		} catch {
			this._userVote = null;
		}

		if (this._confirmBar) {
			this._confirmBar.userVote = this._userVote;
		}
	}

	/* ── Vote ──────────────────────────────────────────────────────────────── */

	async _handleVote(type) {
		if (!this._report || this._isVoting) return;

		this._isVoting = true;

		// Optimistic
		const oldVote = this._userVote;
		this._userVote = type;

		try {
			await createConfirmation({
				report: this._report.id,
				type,
			});

			this.dispatchEvent(
				new CustomEvent("detail-sheet:vote", {
					detail: { reportId: this._report.id, type },
					bubbles: true,
				}),
			);

			// Update counts optimistically
			if (this._report) {
				if (type === "confirm") {
					this._report.confirmation_count =
						(this._report.confirmation_count || 0) + 1;
				} else if (type === "deny") {
					this._report.denial_count = (this._report.denial_count || 0) + 1;
				} else if (type === "resolve") {
					this._report.status = "resolved";
				}
				this._renderReport();
			}

			showToast("Voto registrado", "success");
		} catch (err) {
			console.error("[DetailSheet] Vote error:", err);
			this._userVote = oldVote;

			if (this._confirmBar) {
				this._confirmBar.userVote = oldVote;
			}

			showToast("Error al registrar voto. Intenta de nuevo.", "error");
		} finally {
			this._isVoting = false;
		}
	}

	/* ── Comment ───────────────────────────────────────────────────────────── */

	async _handleComment(body, parentId) {
		if (!this._report) return;

		// La colección `comments` exige `user`: sin él Directus responde
		// "Validation failed for field user. Value is required." y el comentario
		// no se publica. user_created lo rellena Directus solo, pero `user` no.
		const userId = state.user?.id;
		if (!userId) {
			showToast("Inicia sesión para comentar.", "error");
			return;
		}

		try {
			const comment = await createComment({
				report: this._report.id,
				user: userId,
				body,
				...(parentId ? { parent: parentId } : {}),
			});

			this.dispatchEvent(
				new CustomEvent("detail-sheet:comment", {
					detail: { reportId: this._report.id, body, parentId },
					bubbles: true,
				}),
			);

			// Optimistic insert
			this._comments.push(comment);
			if (this._commentThread) {
				this._commentThread.comments = this._comments;
			}
		} catch (err) {
			// El mensaje genérico escondía la causa real (un campo requerido que
			// faltaba); mostrar lo que dice Directus ahorra otra ronda de dudas.
			const detalle = err?.errors?.[0]?.message || err?.message || "";
			console.error("[DetailSheet] Comment error:", detalle, err);
			showToast(
				detalle
					? `No se pudo publicar el comentario: ${detalle}`
					: "No se pudo publicar el comentario.",
				"error",
			);
		}
	}

	/* ── Share ─────────────────────────────────────────────────────────────── */

	_shareReport() {
		if (!this._report) return;

		const url = `${window.location.origin}${window.location.pathname}#/detail/${this._report.id}`;
		const title = this._report.title || "Reporte en AppICEMAP";

		if (navigator.share) {
			navigator.share({ title, url }).catch(() => {
				// Fallback: copy to clipboard
				this._copyToClipboard(url);
			});
		} else {
			this._copyToClipboard(url);
		}
	}

	_copyToClipboard(text) {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(text).then(() => {
				showToast("Enlace copiado al portapapeles", "info");
			});
		} else {
			showToast("Compartir no disponible en este navegador", "info");
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function getDisplayName(report) {
	if (report.display_name_mode === "anonymous") return "Anónimo";
	const user = report.user_created || {};
	const first = user.first_name || "";
	const last = user.last_name || "";
	return [first, last].filter(Boolean).join(" ") || "Usuario";
}

function isLowKarma(report) {
	const user = report.user_created || {};
	return user.is_limited === true && (report.confirmation_count || 0) < 2;
}

function relativeTime(dateStr) {
	if (!dateStr) return "";
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diff = now - then;
	const seconds = Math.floor(diff / 1000);

	if (seconds < 60) return "ahora mismo";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `hace ${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `hace ${hours}h`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `hace ${days}d`;

	return new Date(dateStr).toLocaleDateString("es-MX", {
		month: "short",
		day: "numeric",
	});
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

customElements.define("detail-sheet", DetailSheet);
